'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ProductImages } from './product-images';
import { Breadcrumbs } from '../../homepage/breadcrumbs';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { ProductQuantity } from './product-quantity';
import { ProductStatusText } from '@eliadmin/core/src/components/product/product-status-text';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { ProductQuatro } from './product-quatro';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { ProductAskForProduct } from './product-ask-for-product';
import { BiSolidLike } from 'react-icons/bi';
import { IoGift } from 'react-icons/io5';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { LuDot } from 'react-icons/lu';
import { ProductServices } from '@eliadmin/core/src/components/product/detail/product-services';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ProductAccessoriesVariants } from '@eliadmin/core/src/components/product/detail/product-accessories-variants/product-accessories-variants';
import { ProductQuantityDiscounts } from '@eliadmin/core/src/components/product/detail/product-quantity-discount';
import { lazy, Suspense } from 'react';
import { IvatoProductGifts } from './ivato-product-gifts';

const AskForPriceButton = lazy(() =>
  import('@eliadmin/core/src/components/product/ask-for-price-button').then(module => ({
    default: module.AskForPriceButton,
  })),
);

export function ProductInfo({ product }: { product: ProductDetailData }) {
  const { t } = useTranslations();

  const reviewCount = product.ratingCount ?? product.reviews_count ?? 0;
  const averageRating = Number(product.rating ?? 0);
  const ratingPercentage = Math.round((averageRating / 5) * 100);

  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const desiredObject = selectedVariant ?? product;
  const priceDisplay = getProductPriceDisplay(desiredObject, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });
  const hasSpecialProductStatus = product.status === 'ended' || product.status === 'arrival';
  const askForPrice = priceDisplay.priceOnRequest;

  return (
    <section className={cn('custom-section', 'pb-16 pt-8 sm:pb-20 lg:pb-24')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-12 md:flex-row md:justify-between md:gap-6 lg:gap-10 xl:gap-12',
        )}
      >
        <div className="w-full md:max-w-[610px]">
          <ProductImages />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-6 md:max-w-[580px]">
          <Breadcrumbs
            breadcrumb={product.breadcrumb}
            product={{
              name: product.name,
              slug: product.slug,
            }}
            shorten
          />
          <div className="flex w-full flex-col items-start justify-start gap-1 sm:gap-2">
            <h1 className="text-2xl font-normal text-black sm:text-3xl">
              {selectedVariant?.name || product.name}
            </h1>
            <div className="flex items-center justify-start gap-1 sm:gap-2">
              <div className="flex flex-col gap-1">
                <p className="whitespace-nowrap text-sm font-normal text-[#838383]">
                  {t('text228')} {selectedVariant?.code ?? product?.code}
                </p>
              </div>
              {reviewCount > 0 && (
                <div
                  className={cn(
                    'flex w-full items-center justify-start gap-2',
                    'ea-product-info__rating-container',
                  )}
                >
                  <LuDot size={20} className="text-[#838383]" />
                  <div className="ea-product-review__rating-container">
                    <ProductRating
                      rating={product.rating}
                      className={{
                        starDisabled: 'h-[14px] w-[14px] fill-white stroke-[#FEBB01] stroke-[1px]',
                        starIcon: 'h-[14px] w-[14px] fill-[#FEBB01] stroke-[#FEBB01] stroke-[1px]',
                        starWrapper: 'h-[14px] w-[14px]',
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-start gap-1">
                    <span className="text-sm font-normal text-foreground">{ratingPercentage}%</span>
                    <p className="text-sm font-normal text-[#838383]">({reviewCount})</p>
                  </div>
                </div>
              )}
            </div>
            {product.tags && product.tags?.length > 0 && (
              <div className="flex flex-wrap items-start justify-start gap-1">
                {product.tags?.map(tag => (
                  <div
                    key={tag.name}
                    className="flex items-center justify-center gap-1 rounded-[40px] px-3 py-[6px] text-xs font-normal text-white"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name === 'Doporučujeme' && (
                      <BiSolidLike size={10} className="min-h-[14px] min-w-[14px] text-white" />
                    )}
                    {tag.name === 'Darček' && (
                      <IoGift size={10} className="min-h-[14px] min-w-[14px] text-white" />
                    )}
                    {tag.name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <IvatoProductGifts />
          <ProductAccessoriesVariants />
          <HtmlRenderer
            html={product.short_description}
            className="w-full text-left text-[#838383]"
          />
          <ProductServices />
          {/* <ProductVariants /> */}
          {!hasSpecialProductStatus && !askForPrice && <ProductQuantityDiscounts />}
          <div className="flex w-full flex-col items-start justify-start gap-4 rounded-[8px] bg-[#F5F5F5] p-6 sm:p-8">
            {hasSpecialProductStatus ? (
              <ProductStatusText status={product.status} />
            ) : askForPrice ? (
              <Suspense
                fallback={
                  <span role="status" aria-busy="true">
                    {t('text2278')}
                  </span>
                }
              >
                <AskForPriceButton product={product} />
              </Suspense>
            ) : (
              <>
                {product.availability.id === 1 && product.quantity > 0 && (
                  <div className="flex items-center justify-center gap-1">
                    <p className="hidden text-sm font-normal text-foreground sm:block">
                      {t('text237')}
                    </p>

                    <div className="flex items-center justify-start gap-1">
                      <Image src="/images/available.svg" alt="Dostupne" width={20} height={20} />
                      <p className="text-sm font-medium text-[#57AE00]">
                        <strong className="font-boldd">
                          {t('text2297')} ({product.quantity}ks),{' '}
                        </strong>
                        {t('text2303')}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex w-full flex-col items-start justify-start gap-1">
                  {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
                    <span className="text-sm text-[#838383]">
                      {t('text1926')}{' '}
                      {formatPrice(priceDisplay.mocPrice, currency, currencySettings)}
                    </span>
                  ) : null}
                  <div className="flex items-center justify-start gap-4">
                    <strong className="text-2xl font-bold text-primary sm:text-3xl">
                      {formatPrice(priceDisplay.current, currency, currencySettings)}
                    </strong>
                    {priceDisplay.showPreviousPrice && (
                      <p className="text-xl font-normal text-black line-through sm:text-2xl">
                        {formatPrice(priceDisplay.beforeDiscount, currency, currencySettings)}
                      </p>
                    )}
                  </div>
                  <span className="text-sm font-normal text-[#838383]">
                    {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
                    {formatPrice(priceDisplay.secondary, currency, currencySettings)}{' '}
                    {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
                  </span>
                </div>
                <ProductQuantity />
                {(priceDisplay.currentWithVat ?? 0) >= 100 && (
                  <>
                    <Separator className="h-[1px] w-full bg-border" />
                    <ProductQuatro product={product} />
                  </>
                )}
              </>
            )}

            <Separator className="h-[1px] w-full bg-border" />
            <div
              className={cn(
                'ea-product__actions-container',
                'flex w-full flex-col items-start justify-start gap-6 sm:flex-row sm:items-center',
              )}
            >
              <AddToFavoritesButton
                slug={product.slug}
                isFavorite={product.is_favorite ?? false}
                productId={product.id}
                showText
                className="flex-1 whitespace-nowrap"
              />
              <ProductAskForProduct className="flex-1 whitespace-nowrap" product={product} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
